/**
 * Eva usage API client (SF-3733).
 *
 * Centralizes every Eva admin-usage endpoint and its response shape in one
 * module so later Eva pages (SF-3734/3735/3736) import from here instead of
 * re-deriving paths or types. All endpoints live behind sf-api at
 * `/admin/eva/usage/*` (SF-3731) and are gated by `admin:eva:usage:read`.
 *
 * ⚠️ sf-api wraps every response in `{ success, data }`. The Refine
 * `dataProvider.custom` unwraps the sf-api envelope and returns `{ data:
 * innerPayload }`, which `useCustom` exposes as `query.data`. Hook consumers
 * therefore read the inner payload via `query.data?.data`. Money values are
 * fixed-precision STRINGS to avoid float drift — parse only at display time
 * (see utils/format.ts).
 */
import { useCustom } from '@refinedev/core';
import type { Granularity } from '../../../utils/date';

/** Base path for all Eva usage endpoints on sf-api. */
export const EVA_USAGE_BASE = 'admin/eva/usage';

/** Permission gating the entire Eva section (matches sf-api requirePermissions). */
export const EVA_READ_PERMISSION = 'admin:eva:usage:read';

/**
 * Poll interval (ms) for the live (chat-service-proxied) usage queries so the
 * "this month so far" figures tick up without a manual reload. Shared by every
 * live hook (platform + drill-downs) so the cadence stays consistent (SF-3798
 * set 15s; SF-3734/3735 reuse it for the drill-downs).
 */
export const LIVE_REFETCH_INTERVAL_MS = 15000;

// ---------------------------------------------------------------------------
// Response shapes (mirror sf-api services/insights/evaUsageReadService.js)
// ---------------------------------------------------------------------------

/** Summed token / cost / message totals for a scope+period. Money is a string. */
export interface UsageTotals {
  costUsd: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  messageCount: number;
}

/** Platform totals for a single period (GET /platform). */
export interface PlatformTotals {
  period: string;
  totals: UsageTotals;
  activeUserCount: number;
  totalUsers: number;
}

/**
 * Live platform usage (GET /live/platform), proxied from chat-service so the
 * "this month so far" figure updates without waiting for the nightly cron
 * (SF-3733 AC#2). Unlike {@link PlatformTotals}, chat-service returns the
 * current day and current month as SEPARATE buckets under `daily`/`monthly`
 * (there is NO `totals` key) — the "this month so far" tile reads `monthly`.
 */
export interface PlatformLive {
  scope: string;
  day: string;
  period: string;
  daily: UsageTotals;
  monthly: UsageTotals;
  company_count: number;
  /** This month's spend split by AI feature (SF-4565). Absent before that shipped. */
  byEntryPoint?: LiveEntryPointBreakdown | null;
}

/**
 * One AI feature's share of spend (SF-4566). `requests` counts AI calls
 * (one per chat turn, summary, survey turn or title): only chat turns are
 * messages. Money is a string, as everywhere in this client.
 */
export interface EntryPointTotals {
  costUsd: string;
  requests?: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
}

/**
 * Spend for a month split by AI feature, from the saved monthly figures
 * (GET /platform/entry-points, GET /company/:companyId/entry-points, SF-4566).
 * `entryPoints` is most expensive first. `untagged` is spend recorded before
 * the split existed; the rows plus untagged add up to `totals`.
 */
export interface EntryPointBreakdown {
  period: string;
  companyId?: string;
  totals: EntryPointTotals;
  entryPoints: Array<{ entryPoint: string; totals: EntryPointTotals }>;
  untagged: EntryPointTotals;
}

/**
 * The same split as chat-service sends it on the live endpoints (SF-4565):
 * keyed by entry point rather than a sorted list, money to 2 decimals.
 */
export interface LiveEntryPointBreakdown {
  entryPoints: Record<string, EntryPointTotals>;
  untagged: EntryPointTotals;
}

/** One point in a time series; `bucket` is "YYYY-MM-DD" or "YYYY-MM". */
export interface SeriesPoint extends UsageTotals {
  bucket: string;
  activeUserCount?: number;
}

/** Platform time series (GET /platform/series). */
export interface PlatformSeries {
  granularity: Granularity;
  from: string;
  to: string;
  series: SeriesPoint[];
}

/** One company row in the top-companies table. */
export interface TopCompanyRow {
  companyId: string | null;
  companyName: string | null;
  totals: UsageTotals;
  activeUserCount: number;
}

/** Pagination envelope shared by sf-api list endpoints. */
export interface Pagination {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
}

/** Top companies for a period (GET /platform/top-companies). */
export interface TopCompanies {
  period: string;
  companies: TopCompanyRow[];
  pagination: Pagination;
}

/**
 * One super-admin (SF internal staff) row in the super-admin usage list.
 * Super-admins are NOT attached to a company, so every record here is
 * company-less by design — callers must never assume a company is present.
 */
export interface SuperadminUserRow {
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  totals: UsageTotals;
  /**
   * ISO timestamp of the super-admin's GLOBAL latest Eva activity (one value per
   * user, independent of the selected period), or null when they have never been
   * active. Populated by sf-api from the Redis `usage:user_last_active` hash
   * (SF-3823). Optional — null-guard at the display edge.
   */
  lastActiveAt?: string | null;
}

/** Super-admin usage rollup for a period (GET /superadmins). */
export interface SuperadminUsage {
  period: string;
  totals: UsageTotals;
  users: SuperadminUserRow[];
  pagination: Pagination;
}

/**
 * Per-user time series (GET /user/{userId}/series), SF-3735.
 * Same point shape as the platform series (cost/tokens/messages per bucket);
 * `activeUserCount` is not meaningful at the single-user scope and is omitted
 * by the backend.
 */
export interface UserSeries {
  userId: string;
  granularity: Granularity;
  from: string;
  to: string;
  series: SeriesPoint[];
}

/**
 * One user row in a company's user list (GET /company/:companyId). sf-api
 * computes `percentOfMonthlyCap` server-side as a % of the per-user $10 cap
 * (null when the cap is unconfigured). `lastActiveAt` is the user's GLOBAL
 * latest Eva activity (one value per user, independent of the selected period),
 * or null when they have never been active — populated by sf-api from the Redis
 * `usage:user_last_active` hash (SF-3823). Optional — null-guard at display.
 */
export interface CompanyUserRow {
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  totals: UsageTotals;
  percentOfMonthlyCap: number | null;
  lastActiveAt?: string | null;
}

/** Company totals + paginated user list (GET /company/:companyId). */
export interface CompanyTotals {
  companyId: string;
  period: string;
  totals: UsageTotals;
  activeUserCount: number;
  /** Per-user monthly cap (USD string) used for the per-user cap % column. */
  monthlyCapUsd: string;
  /**
   * Company-level effective monthly spend cap (USD string), used for the
   * "% of company cap" tile. Optional this wave — sf-api returns it only once
   * per-company overrides land (Wave 5). When absent the UI falls back to the
   * env-configured default (see company-detail.tsx). Override-aware by design:
   * sourcing the denominator from data here means no UI change in Wave 5.
   */
  effectiveMonthlyCapUsd?: string | null;
  users: CompanyUserRow[];
  pagination: Pagination;
  /** Live payloads only: this month's spend split by AI feature (SF-4565). */
  byEntryPoint?: LiveEntryPointBreakdown | null;
}

/** A company time series (GET /company/:companyId/series). Same shape as the platform series. */
export type CompanySeries = PlatformSeries;

/**
 * Live current-month company usage (GET /live/company/:companyId), proxied from
 * chat-service's Redis counters so the drill-down tiles + per-user rows update
 * in near real time without waiting for the nightly cron (SF-3734 Wave 2). By
 * design this mirrors the closed-period {@link CompanyTotals} shape — same
 * `totals`, `activeUserCount`, caps, and paginated `users` array — so the page
 * can switch current↔past month WITHOUT reshaping data. Previous/closed months
 * stay on the Mongo rollup ({@link useCompanyTotals}).
 */
export type LiveCompanyUsage = CompanyTotals;

/**
 * Live current-month single-user usage (GET /live/user/:userId), proxied from
 * chat-service (SF-3735 Wave 2). Mirrors the per-user closed-period shape:
 * current-month `totals` plus `percentOfMonthlyCap` (% of the per-user cap,
 * null when the cap is unconfigured). The route returns 404 when the id is a
 * super admin (super-admin usage lives only behind the super-admin endpoints),
 * so callers MUST treat an error as "no live figure" and fall back to the
 * Mongo/series value rather than crashing or surfacing an error toast.
 */
export interface LiveUserUsage {
  userId: string;
  period: string;
  totals: UsageTotals;
  percentOfMonthlyCap: number | null;
}

/**
 * Live current-month super-admin AGGREGATE usage (GET /live/superadmin),
 * proxied from chat-service so the super-admin tiles tick up in near real time.
 * Unlike {@link SuperadminUsage} (the Mongo rollup, which carries a paginated
 * per-user list), the live endpoint is aggregate-only: camelCase `totals` plus
 * a `userCount` of distinct super-admins with live activity this month. There is
 * NO per-user array, so live drives the TILES only — the per-user tables stay
 * rollup-sourced. Every field is optional so the frontend degrades gracefully
 * to the rollup when a field (or the whole response) is absent.
 */
export interface LiveSuperAdminUsage {
  scope?: string;
  period?: string;
  totals?: UsageTotals;
  /** Count of distinct super-admins with live Eva activity this month. */
  userCount?: number;
  /** This month's staff spend split by AI feature. Absent from an older chat-service. */
  byEntryPoint?: LiveEntryPointBreakdown | null;
}

/** One top-user row within a company (GET /company/:companyId/top-users). */
export interface CompanyTopUserRow {
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  totals: UsageTotals;
  /**
   * ISO timestamp of the user's GLOBAL latest Eva activity (see
   * {@link CompanyUserRow.lastActiveAt}), or null. Optional — null-guard at the
   * display edge.
   */
  lastActiveAt?: string | null;
}

/**
 * One row in the LIVE full-roster company view (GET /live/company/:id/roster),
 * SF-3825. Extends {@link CompanyUserRow} with the fields the roster join adds:
 *  - `isActive`: roster membership flag (always true today — the join filters to
 *    active users — but carried explicitly so the table can label rows).
 *  - `effectiveCapUsd`: the user's EFFECTIVE monthly cap (per-user override when
 *    set, else the env default) as a fixed-precision string. `percentOfMonthlyCap`
 *    is computed against THIS cap, not the flat default.
 *  - `isOverride`: whether a per-user override drives `effectiveCapUsd`.
 * Zero-activity users appear here at $0.00 / 0% with a zeroed `totals` rather
 * than being omitted (the whole point of the roster join).
 */
export interface CompanyRosterUserRow extends CompanyUserRow {
  isActive: boolean;
  effectiveCapUsd: string;
  isOverride: boolean;
}

/**
 * Live current-month FULL-ROSTER company usage (GET /live/company/:id/roster),
 * SF-3825. Unlike {@link LiveCompanyUsage} (spenders only), this enumerates the
 * entire active, non-super-admin roster, left-joined to live spend and sorted by
 * live cost desc. sf-api owns pagination over the full roster. On a live-call
 * failure sf-api degrades to the roster with zeroed spend plus a soft `warning`.
 */
export interface LiveCompanyRoster {
  companyId: string;
  period: string | null;
  totals: UsageTotals | null;
  /** chat-service's count of users who SPENT this month (best-effort, may be null when degraded). */
  activeUserCount: number | null;
  /** Full active roster size — what the table paginates over. */
  rosterUserCount: number;
  /** Per-user DEFAULT monthly cap (USD string); per-row effectiveCapUsd may override it. */
  monthlyCapUsd: string;
  users: CompanyRosterUserRow[];
  pagination: Pagination;
  /** Present only when live spend was unavailable and the roster degraded to zeroed spend. */
  warning?: string;
}

/** Top users within a company for a period (GET /company/:companyId/top-users). */
export interface CompanyTopUsers {
  companyId: string;
  period: string;
  users: CompanyTopUserRow[];
  pagination: Pagination;
}

/**
 * One tool row in the top-tools ranking (SF-3775). Mirrors the bounded `tools`
 * array on sf-api's EvaToolUsage{Daily,Monthly} models: just a tool name and an
 * invocation count — there is no cost dimension on these rollups.
 */
export interface TopToolRow {
  tool: string;
  count: number;
}

/**
 * Top Eva tools by invocation count for a period (GET /insights/usage/top-tools).
 * Platform-wide when `company_id` is null, or a per-company breakdown otherwise;
 * `superadmin` when staff (super admin) tool counts were asked for (SF-4855).
 */
export interface TopTools {
  scope: 'platform' | 'company' | 'superadmin';
  company_id: string | null;
  granularity: Granularity;
  period: string;
  tools: TopToolRow[];
  pagination: Pagination;
}

// ---------------------------------------------------------------------------
// Hooks — one per endpoint. Each is null-safe and disable-able via `enabled`.
// ---------------------------------------------------------------------------

/** Common options shared by the Eva hooks. */
interface EnabledOption {
  /** Gate the request (e.g. on access-control resolution). Defaults to true. */
  enabled?: boolean;
}

/**
 * Fetch platform totals for a period.
 * @param period - "YYYY-MM", "this-month", or "last-month"
 */
export function usePlatformTotals(period: string, options: EnabledOption = {}) {
  return useCustom<PlatformTotals>({
    url: `/${EVA_USAGE_BASE}/platform`,
    method: 'get',
    config: { query: { period } },
    queryOptions: { enabled: options.enabled ?? true },
  });
}

/**
 * Fetch live platform totals for the current period ("this month so far").
 *
 * Unlike {@link usePlatformTotals}, this reads sf-api's live endpoint
 * (`/admin/eva/usage/live/platform`), which sf-api proxies from chat-service's
 * current-period usage so the figure updates without waiting for the nightly
 * cron (SF-3733 AC#2). chat-service returns `daily`/`monthly` buckets (see
 * {@link PlatformLive}); the tile reads `monthly`. Polls every 15s so the tile
 * auto-updates without a manual reload.
 */
export function usePlatformLive(options: EnabledOption = {}) {
  return useCustom<PlatformLive>({
    url: `/${EVA_USAGE_BASE}/live/platform`,
    method: 'get',
    queryOptions: {
      enabled: options.enabled ?? true,
      // Poll so the "this month so far" tile reflects fresh chat-service usage
      // without a manual reload; pause while the tab is backgrounded.
      refetchInterval: LIVE_REFETCH_INTERVAL_MS,
      refetchIntervalInBackground: false,
    },
    // The live "this month so far" figure is supplementary: the page degrades
    // the tile on its own and excludes this query from its error state, so a
    // missing/unreachable live endpoint must NOT raise a global error toast.
    errorNotification: false,
  });
}

/**
 * Fetch LIVE current-month company usage (GET /live/company/:companyId),
 * proxied from chat-service so the company drill-down's this-month-spend tile
 * and per-user rows update in near real time (SF-3734 Wave 2). Mirrors
 * {@link usePlatformLive}: polls on {@link LIVE_REFETCH_INTERVAL_MS} (paused in
 * the background) and suppresses the global error notification because the page
 * degrades to the Mongo rollup ({@link useCompanyTotals}) on failure. The
 * response mirrors {@link CompanyTotals} (same totals + paginated `users`), so
 * the page swaps current↔past month without reshaping data. Only call this for
 * the CURRENT month — previous/closed months stay on the Mongo rollup.
 *
 * @param companyId - company ObjectId
 * @param pagination - 1-based page + page size for the user list (matched to the rollup call)
 */
export function useLiveCompanyUsage(
  companyId: string,
  pagination: { page: number; pageSize: number },
  options: EnabledOption = {},
) {
  const offset = Math.max(0, (pagination.page - 1) * pagination.pageSize);
  return useCustom<LiveCompanyUsage>({
    url: `/${EVA_USAGE_BASE}/live/company/${companyId}`,
    method: 'get',
    config: { query: { limit: pagination.pageSize, offset } },
    queryOptions: {
      enabled: (options.enabled ?? true) && !!companyId,
      refetchInterval: LIVE_REFETCH_INTERVAL_MS,
      refetchIntervalInBackground: false,
    },
    errorNotification: false,
  });
}

/**
 * Fetch the LIVE current-month FULL-ROSTER company usage
 * (GET /live/company/:companyId/roster), SF-3825. Unlike
 * {@link useLiveCompanyUsage} (spenders only), sf-api enumerates the entire
 * active, non-super-admin roster, left-joins live spend (zero-filling absentees),
 * annotates each row with its EFFECTIVE per-user cap, sorts by live cost desc,
 * and paginates server-side. Mirrors the live polling + suppressed-error-toast
 * behaviour; on a live-call failure sf-api still returns the roster (zeroed
 * spend + `warning`), so the table degrades gracefully rather than emptying.
 *
 * @param companyId - company ObjectId
 * @param pagination - 1-based page + page size for the roster list
 * @param options - enabled flag plus an optional case-insensitive email
 *   `search` substring. sf-api (SF-3841) applies it over the full roster
 *   BEFORE pagination, so total/pages reflect the matched set; a blank/omitted
 *   term returns the full roster. Threading it through `config.query` keeps it
 *   in the TanStack Query key, so changing the term refetches automatically.
 */
export function useLiveCompanyRoster(
  companyId: string,
  pagination: { page: number; pageSize: number },
  options: EnabledOption & { search?: string } = {},
) {
  const offset = Math.max(0, (pagination.page - 1) * pagination.pageSize);
  const search = options.search?.trim() ?? '';
  return useCustom<LiveCompanyRoster>({
    url: `/${EVA_USAGE_BASE}/live/company/${companyId}/roster`,
    method: 'get',
    config: { query: { limit: pagination.pageSize, offset, ...(search ? { search } : {}) } },
    queryOptions: {
      enabled: (options.enabled ?? true) && !!companyId,
      refetchInterval: LIVE_REFETCH_INTERVAL_MS,
      refetchIntervalInBackground: false,
    },
    errorNotification: false,
  });
}

/**
 * Fetch LIVE current-month single-user usage (GET /live/user/:userId), proxied
 * from chat-service so the user drawer's this-month-spend tile + %-of-cap update
 * in near real time (SF-3735 Wave 2). Mirrors {@link usePlatformLive}: polls on
 * {@link LIVE_REFETCH_INTERVAL_MS} (paused in the background) and suppresses the
 * global error notification — the drawer degrades to the Mongo series value on
 * failure. NOTE: the route 404s for super-admin ids; with `errorNotification`
 * off and the page's own fallback, that surfaces as `isError` and the caller
 * simply drops the live badge and keeps the series-derived figure. Disabled
 * when `userId` is empty so the drawer can mount before a user is selected.
 */
export function useLiveUserUsage(
  userId: string | null | undefined,
  options: EnabledOption = {},
) {
  return useCustom<LiveUserUsage>({
    url: `/${EVA_USAGE_BASE}/live/user/${userId}`,
    method: 'get',
    queryOptions: {
      enabled: (options.enabled ?? true) && Boolean(userId),
      refetchInterval: LIVE_REFETCH_INTERVAL_MS,
      refetchIntervalInBackground: false,
      // Super-admin ids 404 here; do not retry a deterministic 404.
      retry: false,
    },
    errorNotification: false,
  });
}

/**
 * Fetch LIVE current-month super-admin aggregate usage (GET /live/superadmin),
 * proxied from chat-service. Mirrors {@link usePlatformLive} (polls, no global
 * error toast) and {@link SuperadminUsage} in shape. Optional this wave — wired
 * by the super-admin view when it adopts live current-month figures.
 */
export function useLiveSuperAdminUsage(options: EnabledOption = {}) {
  return useCustom<LiveSuperAdminUsage>({
    url: `/${EVA_USAGE_BASE}/live/superadmin`,
    method: 'get',
    queryOptions: {
      enabled: options.enabled ?? true,
      refetchInterval: LIVE_REFETCH_INTERVAL_MS,
      refetchIntervalInBackground: false,
    },
    errorNotification: false,
  });
}

/**
 * Fetch the platform time series for a date range + granularity.
 */
export function usePlatformSeries(
  params: { from: string; to: string; granularity: Granularity },
  options: EnabledOption = {},
) {
  return useCustom<PlatformSeries>({
    url: `/${EVA_USAGE_BASE}/platform/series`,
    method: 'get',
    config: { query: params },
    queryOptions: { enabled: options.enabled ?? true },
  });
}

/**
 * Fetch the top-N companies by cost for a period.
 * @param period - "YYYY-MM", "this-month", or "last-month"
 * @param limit - number of companies (server clamps; default 5)
 */
export function useTopCompanies(period: string, limit = 5, options: EnabledOption = {}) {
  return useCustom<TopCompanies>({
    url: `/${EVA_USAGE_BASE}/platform/top-companies`,
    method: 'get',
    config: { query: { period, limit } },
    queryOptions: { enabled: options.enabled ?? true },
  });
}

/**
 * Fetch super-admin (SF internal staff) usage for a period: aggregate totals
 * plus a paginated per-super-admin list. Super-admin data lives ONLY behind
 * this endpoint and is never mixed into platform/company totals (SF-3731).
 * @param period - "YYYY-MM", "this-month", or "last-month"
 * @param pagination - server-side limit/offset for the user list
 */
export function useSuperadmins(
  period: string,
  pagination: { limit: number; offset: number },
  options: EnabledOption = {},
) {
  return useCustom<SuperadminUsage>({
    url: `/${EVA_USAGE_BASE}/superadmins`,
    method: 'get',
    config: { query: { period, ...pagination } },
    queryOptions: { enabled: options.enabled ?? true },
  });
}

/**
 * Fetch the super-admin usage time series for a date range + granularity.
 */
export function useSuperadminsSeries(
  params: { from: string; to: string; granularity: Granularity },
  options: EnabledOption = {},
) {
  return useCustom<PlatformSeries>({
    url: `/${EVA_USAGE_BASE}/superadmins/series`,
    method: 'get',
    config: { query: params },
    queryOptions: { enabled: options.enabled ?? true },
  });
}

/**
 * Fetch a single user's usage time series for a date range + granularity
 * (GET /user/{userId}/series), SF-3735. Used by the user detail drawer for both
 * the 30-day daily and 12-month monthly charts, and by the super-admin drill-in.
 * Disabled when `userId` is empty so the drawer can mount before a user is
 * selected without firing a request.
 */
export function useUserSeries(
  userId: string | null | undefined,
  params: { from: string; to: string; granularity: Granularity },
  options: EnabledOption = {},
) {
  return useCustom<UserSeries>({
    url: `/${EVA_USAGE_BASE}/user/${userId}/series`,
    method: 'get',
    config: { query: params },
    queryOptions: { enabled: (options.enabled ?? true) && Boolean(userId) },
  });
}

/**
 * Fetch company totals for a period plus a paginated user list.
 * @param companyId - company ObjectId
 * @param period - "YYYY-MM", "this-month", or "last-month"
 * @param pagination - 1-based page + page size for the user list
 */
export function useCompanyTotals(
  companyId: string,
  period: string,
  pagination: { page: number; pageSize: number },
  options: EnabledOption = {},
) {
  const offset = Math.max(0, (pagination.page - 1) * pagination.pageSize);
  return useCustom<CompanyTotals>({
    url: `/${EVA_USAGE_BASE}/company/${companyId}`,
    method: 'get',
    config: { query: { period, limit: pagination.pageSize, offset } },
    queryOptions: { enabled: (options.enabled ?? true) && !!companyId },
  });
}

/** Fetch a company's time series for a date range + granularity. */
export function useCompanySeries(
  companyId: string,
  params: { from: string; to: string; granularity: Granularity },
  options: EnabledOption = {},
) {
  return useCustom<CompanySeries>({
    url: `/${EVA_USAGE_BASE}/company/${companyId}/series`,
    method: 'get',
    config: { query: params },
    queryOptions: { enabled: (options.enabled ?? true) && !!companyId },
  });
}

/**
 * Fetch the top-N users by cost within a company for a period.
 * @param limit - number of users (server clamps; default 5)
 */
export function useCompanyTopUsers(
  companyId: string,
  period: string,
  limit = 5,
  options: EnabledOption = {},
) {
  return useCustom<CompanyTopUsers>({
    url: `/${EVA_USAGE_BASE}/company/${companyId}/top-users`,
    method: 'get',
    config: { query: { period, limit } },
    queryOptions: { enabled: (options.enabled ?? true) && !!companyId },
  });
}

/**
 * Fetch platform spend for a month split by AI feature, from the saved monthly
 * figures (SF-4566). For the current month the page uses the live split on
 * {@link usePlatformLive} instead, falling back to this when live fails.
 * @param period - "YYYY-MM", "this-month", or "last-month"
 */
export function usePlatformEntryPoints(period: string, options: EnabledOption = {}) {
  return useCustom<EntryPointBreakdown>({
    url: `/${EVA_USAGE_BASE}/platform/entry-points`,
    method: 'get',
    config: { query: { period } },
    queryOptions: { enabled: options.enabled ?? true },
    // The section shows its own "unavailable" state, and for the current month
    // this is only the fallback behind live, so no global error toast.
    errorNotification: false,
  });
}

/**
 * Fetch staff (super admin) spend for a month split by AI feature, from the
 * saved monthly figures (SF-4855). Same shape as {@link usePlatformEntryPoints};
 * for the current month the page prefers the live split on
 * {@link useLiveSuperAdminUsage}, falling back to this when live fails.
 * @param period - "YYYY-MM", "this-month", or "last-month"
 */
export function useSuperadminEntryPoints(period: string, options: EnabledOption = {}) {
  return useCustom<EntryPointBreakdown>({
    url: `/${EVA_USAGE_BASE}/superadmins/entry-points`,
    method: 'get',
    config: { query: { period } },
    queryOptions: { enabled: options.enabled ?? true },
    // Same as usePlatformEntryPoints: the section handles its own failure.
    errorNotification: false,
  });
}

/**
 * Fetch one company's spend for a month split by AI feature, from the saved
 * monthly figures (SF-4566). The current month prefers the live split on
 * {@link useLiveCompanyUsage}.
 */
export function useCompanyEntryPoints(
  companyId: string,
  period: string,
  options: EnabledOption = {},
) {
  return useCustom<EntryPointBreakdown>({
    url: `/${EVA_USAGE_BASE}/company/${companyId}/entry-points`,
    method: 'get',
    config: { query: { period } },
    queryOptions: { enabled: (options.enabled ?? true) && !!companyId },
    // Same as usePlatformEntryPoints: the section handles its own failure.
    errorNotification: false,
  });
}

/**
 * Fetch the top-N Eva tools by invocation count for a period (SF-3775).
 *
 * Unlike the other hooks here, this reads the shared insights endpoint
 * (`/insights/usage/top-tools`) rather than the `/admin/eva/usage/*` family —
 * the tool-usage rollups live behind insights. The endpoint requires an
 * explicit `granularity` + `period` (it does NOT accept the "this-month"
 * keyword), so callers pass a concrete "YYYY-MM" (monthly) / "YYYY-MM-DD"
 * (daily) bucket. Platform-wide by default; pass `companyId` for a per-company
 * breakdown. `limit` is server-clamped — we bound it here so the request is
 * never unbounded.
 *
 * @param period - explicit bucket: "YYYY-MM" (monthly) or "YYYY-MM-DD" (daily)
 * @param granularity - bucket size matching `period` (default 'monthly')
 * @param limit - number of tools (server clamps; default 10)
 * @param companyId - optional company ObjectId for a per-company breakdown
 * @param options.scope - `superadmin` ranks staff (super admin) tool use
 *   instead of the platform's (SF-4855), read from the Eva usage admin route
 *   behind the usage read permission; `companyId` is ignored with it
 */
export function useTopTools(
  period: string,
  granularity: Granularity = 'monthly',
  limit = 10,
  companyId?: string,
  options: EnabledOption & { scope?: 'superadmin' } = {},
) {
  // Staff tool use lives on the Eva usage admin routes (same permission as the
  // rest of the Super-Admins page), not on the signed-in-only insights route.
  const staff = options.scope === 'superadmin';
  return useCustom<TopTools>({
    url: staff ? `/${EVA_USAGE_BASE}/superadmins/top-tools` : '/insights/usage/top-tools',
    method: 'get',
    // company_id is omitted (not sent as undefined) when absent so the request
    // stays platform-wide; sf-api treats a missing company_id as the platform
    // ranking.
    config: {
      query: {
        granularity,
        period,
        limit,
        ...(companyId && !staff ? { company_id: companyId } : {}),
      },
    },
    queryOptions: { enabled: (options.enabled ?? true) && !!period },
  });
}
